import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ChevronDown, ChevronUp, UserCog, Pencil } from 'lucide-react';

import { supabase } from '@/integrations/supabase/client';
import {
  formatReportFieldValue,
  labelOfReportField,
  type ReportChangeLog,
} from '@/utils/reportEditUtils';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface ReportChangeLogsDialogProps {
  reportId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type LogType = ReportChangeLog['type'];
type LogStatus = ReportChangeLog['status'];

const TYPE_LABEL: Record<LogType, string> = {
  MANUAL: 'Admin แก้โดยตรง',
  TEACHER_REQUEST: 'ครูขอแก้ไข',
};

const TYPE_BADGE_CLASS: Record<LogType, string> = {
  MANUAL: 'bg-blue-100 text-blue-700 border-blue-200',
  TEACHER_REQUEST: 'bg-amber-100 text-amber-700 border-amber-200',
};

const STATUS_LABEL: Record<LogStatus, string> = {
  PENDING: 'รอตรวจสอบ',
  APPROVED: 'อนุมัติแล้ว',
  REJECTED: 'ไม่อนุมัติ',
  CANCELLED: 'ยกเลิก',
};

const STATUS_BADGE_CLASS: Record<LogStatus, string> = {
  PENDING: 'bg-amber-100 text-amber-700 border-amber-200',
  APPROVED: 'bg-green-100 text-green-700 border-green-200',
  REJECTED: 'bg-red-100 text-red-700 border-red-200',
  CANCELLED: 'bg-gray-100 text-gray-600 border-gray-200',
};

export const ReportChangeLogsDialog: React.FC<ReportChangeLogsDialogProps> = ({
  reportId,
  open,
  onOpenChange,
}) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const { data: logs = [], isLoading } = useQuery({
    queryKey: ['report-change-logs', reportId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('report_change_logs')
        .select('*')
        .eq('report_id', reportId)
        .order('created_at', { ascending: false });
      if (error) {
        console.warn('Could not load report_change_logs:', error.message);
        return [] as ReportChangeLog[];
      }
      return (data ?? []) as ReportChangeLog[];
    },
    enabled: open && !!reportId,
  });

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>ประวัติการแก้ไขรายงาน</DialogTitle>
          <DialogDescription>
            รายการขอแก้ไขทั้งหมดของรายงานนี้ — เรียงจากเก่าไปใหม่
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="py-10 text-center text-sm text-gray-500">กำลังโหลด...</div>
        ) : logs.length === 0 ? (
          <div className="py-10 text-center text-sm text-gray-500">
            ยังไม่มีประวัติการแก้ไข
          </div>
        ) : (
          <div className="space-y-3">
            {logs.map((log, idx) => (
              <LogEntry
                key={log.id}
                log={log}
                index={idx + 1}
                isExpanded={expanded.has(log.id)}
                onToggle={() => toggleExpanded(log.id)}
              />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

interface LogEntryProps {
  log: ReportChangeLog;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
}

const LogEntry: React.FC<LogEntryProps> = ({ log, index, isExpanded, onToggle }) => {
  const changedFields = useMemo(() => Object.keys(log.after ?? {}), [log.after]);
  const Icon = log.type === 'MANUAL' ? UserCog : Pencil;

  return (
    <div className="rounded-md border p-3 space-y-2">
      <div className="flex flex-wrap items-start gap-2 justify-between">
        <div className="flex items-start gap-2">
          <div className="text-xs text-gray-400 mt-0.5 font-mono">#{index}</div>
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className={TYPE_BADGE_CLASS[log.type]}>
                <Icon className="h-3 w-3 mr-1" />
                {TYPE_LABEL[log.type] ?? log.type}
              </Badge>
              <Badge variant="outline" className={STATUS_BADGE_CLASS[log.status]}>
                {STATUS_LABEL[log.status] ?? log.status}
              </Badge>
            </div>
            <div className="text-xs text-gray-600">
              <span className="text-gray-500">โดย:</span>{' '}
              <span className="font-medium break-all">{log.created_by}</span>{' '}
              <span className="text-gray-400">
                · {format(new Date(log.created_at), 'dd/MM/yyyy HH:mm')}
              </span>
            </div>
            {log.reviewed_by && log.reviewed_at && log.type === 'TEACHER_REQUEST' && (
              <div className="text-xs text-gray-600">
                <span className="text-gray-500">
                  {log.status === 'APPROVED'
                    ? 'อนุมัติโดย:'
                    : log.status === 'REJECTED'
                      ? 'ไม่อนุมัติโดย:'
                      : 'จัดการโดย:'}
                </span>{' '}
                <span className="font-medium break-all">{log.reviewed_by}</span>{' '}
                <span className="text-gray-400">
                  · {format(new Date(log.reviewed_at), 'dd/MM/yyyy HH:mm')}
                </span>
              </div>
            )}
          </div>
        </div>
        {changedFields.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggle}
            className="h-7 px-2 text-xs"
          >
            แก้ {changedFields.length} ฟิลด์
            {isExpanded ? (
              <ChevronUp className="h-3 w-3 ml-1" />
            ) : (
              <ChevronDown className="h-3 w-3 ml-1" />
            )}
          </Button>
        )}
      </div>

      {log.reason && (
        <div className="text-xs bg-gray-50 rounded px-2 py-1.5">
          <span className="text-gray-500">เหตุผล:</span>{' '}
          <span className="text-gray-700 whitespace-pre-wrap">{log.reason}</span>
        </div>
      )}

      {isExpanded && changedFields.length > 0 && (
        <div className="rounded-md border overflow-x-auto bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-1/4 text-xs">ฟิลด์</TableHead>
                <TableHead className="text-xs">ค่าเดิม</TableHead>
                <TableHead className="text-xs">ค่าใหม่</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {changedFields.map((field) => (
                <TableRow key={field}>
                  <TableCell className="font-medium text-xs">
                    {labelOfReportField(field)}
                  </TableCell>
                  <TableCell className="text-xs text-gray-500 whitespace-pre-wrap">
                    {formatReportFieldValue(log.before?.[field])}
                  </TableCell>
                  <TableCell className="text-xs font-medium whitespace-pre-wrap">
                    {formatReportFieldValue(log.after?.[field])}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
